import type { LearnFAQ } from "@/pages/learn/learnShared";
import {
  makeEsArticle,
  esSaintRelated,
  type EsArticleInput,
} from "./learnFactoryEs";

const reviewBlock = {
  writtenBy: "Neuro Wellness TMS Centers of America",
  reviewedBy: "Daniel Pietras, MD",
  published: "November 3, 2026",
  lastReviewed: "November 3, 2026",
  lastUpdated: "November 3, 2026",
};

const faqs: LearnFAQ[] = [
  {
    question: "¿Qué cuenta como tratamiento avanzado para la depresión?",
    answer:
      "Tratamientos como TMS, SAINT® TMS acelerada, SPRAVATO® (esketamina) y ECT, que actúan por mecanismos distintos a los antidepresivos orales. TMS y SAINT® están autorizados por la FDA; SPRAVATO® está aprobado por la FDA para la depresión resistente al tratamiento. Un psiquiatra determina cuál es apropiado según su historial clínico.",
  },
  {
    question: "¿La TMS es un tratamiento avanzado para la depresión?",
    answer:
      "Sí. La TMS está autorizada por la FDA para adultos con trastorno depresivo mayor que no han mejorado con antidepresivos. Usa impulsos magnéticos, no medicación, y no causa efectos secundarios sistémicos. SAINT® es un protocolo de TMS acelerada de cinco días autorizado por la FDA (K220177).",
  },
  {
    question:
      "¿La depresión avanzada es lo mismo que la depresión resistente al tratamiento?",
    answer:
      "Se relacionan. La depresión resistente al tratamiento es la que no responde a al menos dos antidepresivos en dosis y duración adecuadas. Los tratamientos 'avanzados' se consideran cuando los de primera línea no han funcionado.",
  },
];

export const esAdvancedDepressionTreatment = makeEsArticle({
  slug: "advanced-depression-treatment",
  title: "Opciones de tratamiento avanzado para la depresión | Neuro Wellness",
  description:
    "Tratamientos avanzados: TMS, SAINT® TMS acelerada, SPRAVATO® y otras opciones para la depresión resistente al tratamiento cuando los tratamientos estándar no han funcionado.",
  h1: "Opciones de tratamiento avanzado para la depresión",
  breadcrumbLabel: "Tratamiento avanzado para la depresión",
  shortAnswer:
    "Los tratamientos avanzados son opciones para personas cuya depresión no ha respondido a los tratamientos de primera línea, como antidepresivos y psicoterapia. Incluyen TMS (estimulación magnética transcraneal), SAINT® TMS acelerada, SPRAVATO® (esketamina), aumento farmacológico y ECT. Actúan por mecanismos distintos a los antidepresivos orales y están autorizados o aprobados por la FDA para la depresión resistente al tratamiento. El tratamiento apropiado depende de su historial clínico y lo determina un psiquiatra.",
  keyTakeaways: [
    "Los tratamientos avanzados son para depresiones que no han respondido al tratamiento estándar.",
    "TMS y SAINT® usan estimulación magnética del cerebro.",
    "SPRAVATO® actúa mediante la modulación de receptores NMDA.",
    "ECT es el más eficaz para depresión grave o psicótica.",
    "El tratamiento apropiado lo determina un psiquiatra.",
  ],
  sections: [
    {
      heading: "Qué hace que un tratamiento sea 'avanzado'",
      paragraphs: [
        "Los tratamientos avanzados son opciones que se utilizan cuando los tratamientos de primera línea —antidepresivos y psicoterapia— no han producido alivio suficiente. Actúan por mecanismos distintos a los antidepresivos orales: estimulación cerebral (TMS, SAINT®, ECT) o vías químicas diferentes (SPRAVATO®/esketamina). Generalmente se consideran tras fracasar al menos dos ensayos adecuados de antidepresivos.",
        "Avanzado no significa experimental. TMS, SAINT®, SPRAVATO® y ECT son tratamientos autorizados o aprobados por la FDA con evidencia que respalda su uso para indicaciones específicas.",
      ],
    },
    {
      heading: "TMS (estimulación magnética transcraneal)",
      paragraphs: [
        "La TMS está autorizada por la FDA para adultos con trastorno depresivo mayor que no han mejorado con antidepresivos. Usa impulsos magnéticos para estimular la corteza prefrontal dorsolateral izquierda. Un curso estándar es de aproximadamente 36 sesiones durante seis semanas. La TMS no es invasiva, no requiere anestesia y suele estar cubierta por el seguro para la depresión resistente al tratamiento.",
      ],
    },
    {
      heading: "SAINT® TMS acelerada",
      paragraphs: [
        "SAINT® es un protocolo de TMS acelerada —50 sesiones en cinco días con orientación por fMRI. Está autorizado por la FDA (K220177) para adultos cuya depresión no ha mejorado con antidepresivos. En un ensayo controlado con simulación, el 79 % del grupo activo alcanzó la remisión. SAINT® es el protocolo de tratamiento para la depresión autorizado por la FDA más rápido.",
      ],
    },
    {
      heading: "SPRAVATO® (esketamina)",
      paragraphs: [
        "SPRAVATO® es un aerosol nasal aprobado por la FDA para la depresión resistente al tratamiento. Actúa mediante la modulación de receptores NMDA, un mecanismo distinto al de los antidepresivos estándar. Se administra en consulta bajo supervión, generalmente dos veces por semana durante la fase de inducción. Puede ser una opción para pacientes que no han respondido a antidepresivos orales.",
      ],
    },
    {
      heading: "ECT (terapia electroconvulsiva)",
      paragraphs: [
        "La ECT es el tratamiento no invasivo más eficaz para la depresión grave o psicótica. Se realiza bajo anestesia y es muy eficaz, pero conlleva más efectos secundarios cognitivos que la TMS o SAINT®. Se reserva para casos graves o cuando se necesita una respuesta rápida.",
      ],
    },
  ],
  faqs,
  sources: [],
  relatedLinks: esSaintRelated,
  reviewBlock,
} satisfies EsArticleInput);
